import BaseModel from './BaseModel';
import { EntityTypeEnum } from '../utilities/Enumerables';
import { supabase } from '../utilities/supabase';

// Set only by the Team Members RPCs (team_deactivate_v1 / team_reactivate_v1 ...).
// Mapped read-only in fromDatabase; update() strips them in case toDatabase() ever
// grows to include them.
const READ_ONLY_COLUMNS = [
  'deactivated_at',
  'deactivated_by',
  'deactivation_reason',
  'previous_role'
];

/**
 * Represents a User Profile in the system.
 * @extends BaseModel
 */
class UserProfile extends BaseModel {
  /** @type {string} The name of the database table for User Profiles */
  static table = 'user_profiles';

  /** What a person whose every profile is deactivated is told (App.jsx, Login.jsx). */
  static ACCESS_REMOVED_MESSAGE =
    'Your access to the CRM has been removed. Contact an administrator.';

  /**
   * True for the sign-in error of a deactivated login: team_deactivate_v1 bans it,
   * so Supabase Auth refuses the password with `user_banned` ("User is banned").
   * @param {{ code?: string, message?: string } | null | undefined} error
   * @returns {boolean}
   */
  static isAccessRemovedError(error) {
    return error?.code === 'user_banned' || /\bbanned\b/i.test(error?.message || '');
  }

  static sortFields = {
    'company.name': 'companies.name',
    'trainer.name': 'trainers.name'
  };

  /** @type {string} The SQL query to select User Profile data */
  static selectQuery =
    '*, role:permission_groups!inner(*), companies!user_profiles_company_id_fkey(id,name), trainers!user_profiles_trainer_id_fkey(id,name)';

  /**
   * Creates a UserProfile instance from database format data
   * @param {Object} data - The database format user profile data
   * @returns {UserProfile} A new UserProfile instance
   */
  static fromDatabase(data = {}) {
    const userProfile = new UserProfile();
    userProfile.id = data.id;
    userProfile.mergedIntoId = data.merged_into_id;
    userProfile.createdDate = data.created_date;
    userProfile.updatedDate = data.updated_date;
    userProfile.auth = data.auth;
    userProfile.firstName = data.first_name;
    userProfile.lastName = data.last_name;
    userProfile.nickname = data.nickname;
    userProfile.fullName = data.full_name;
    userProfile.email = data.email;
    userProfile.personalEmail = data.personal_email;
    userProfile.phones = data.phones || [];
    userProfile.profilePhoto = data.profile_photo;
    userProfile.companyId = data.company_id;
    userProfile.company = data.companies
      ? { id: data.companies.id, name: data.companies.name }
      : null;
    userProfile.trainerId = data.trainer_id;
    userProfile.trainer = data.trainers
      ? {
          id: data.trainers.id,
          name: data.trainers.name
        }
      : null;
    userProfile.levy = data.levy;
    userProfile.metrics = data.metrics || {};
    userProfile.active = data.active;
    userProfile.displayId = data.display_id;
    userProfile.bohsaAdmin = data.bohsa_admin;
    userProfile.bohsaEntity = data.bohsa_entity ?? false;
    userProfile.companyAdmin = data.company_admin;
    userProfile.role = data.role;
    userProfile.crmPreferences = data.crm_preferences;
    userProfile.dateOfBirth = data.date_of_birth;
    userProfile.nationalInsurance = data.national_insurance;
    userProfile.homeAddress = data.home_address;
    userProfile.metadata = data.metadata;
    userProfile.currentEmployee = data.current_employee;
    userProfile.currentTrainer = data.current_trainer;
    userProfile.emailPreferences = data.email_preferences;
    // Deactivation (Settings -> Team Members). Read-only: written only by the
    // team_*_v1 RPCs, never sent by toDatabase()/update() (see READ_ONLY_COLUMNS).
    userProfile.deactivatedAt = data.deactivated_at ?? null;
    userProfile.deactivatedBy = data.deactivated_by ?? null;
    userProfile.deactivationReason = data.deactivation_reason ?? null;
    userProfile.previousRole = data.previous_role ?? null;
    return userProfile;
  }

  /**
   * Creates an instance of UserProfile.
   * @param {Object} data - The user profile data.
   */
  constructor(data = {}) {
    super(data);
    this.auth = data.auth ?? this.auth ?? undefined;
    this.firstName = data.firstName ?? this.firstName ?? undefined;
    this.lastName = data.lastName ?? this.lastName ?? undefined;
    // What they like to be called; emails greet them by this instead of firstName when set.
    this.nickname = data.nickname ?? this.nickname ?? undefined;
    this.fullName = data.fullName ?? this.fullName ?? undefined;
    this.email = data.email ?? this.email ?? undefined;
    // Informational only; email stays the work address logins and emails use.
    this.personalEmail = data.personalEmail ?? this.personalEmail ?? undefined;
    this.phones = data.phones ?? this.phones ?? [];
    this.profilePhoto = data.profilePhoto ?? this.profilePhoto ?? undefined;
    this.companyId = data.companyId ?? this.companyId ?? undefined;
    this.company = data.company ?? this.company ?? undefined;
    this.levy = data.levy ?? this.levy ?? undefined;
    this.trainerId = data.trainerId ?? this.trainerId ?? undefined;
    this.trainer = data.trainer ?? this.trainer ?? undefined;
    this.metrics = data.metrics ?? this.metrics ?? {};
    this.active = data.active ?? this.active ?? false;
    this.displayId = data.displayId ?? this.displayId ?? undefined;
    this.bohsaAdmin = data.bohsaAdmin ?? this.bohsaAdmin ?? false;
    // Read-only classification: routine profile saves must not overwrite it.
    this.bohsaEntity = data.bohsaEntity ?? this.bohsaEntity ?? false;
    this.companyAdmin = data.companyAdmin ?? this.companyAdmin ?? false;
    this.role = data.role ?? this.role ?? undefined;
    this.crmPreferences = data.crmPreferences ?? this.crmPreferences ?? {};
    this.dateOfBirth = data.dateOfBirth ?? this.dateOfBirth ?? undefined;
    this.nationalInsurance =
      data.nationalInsurance ?? this.nationalInsurance ?? undefined;
    this.homeAddress = data.homeAddress ?? this.homeAddress ?? {};
    this.metadata = data.metadata ?? this.metadata ?? {};
    this.currentEmployee =
      data.currentEmployee ?? this.currentEmployee ?? false;
    this.currentTrainer = data.currentTrainer ?? this.currentTrainer ?? false;
    this.emailPreferences =
      data.emailPreferences ?? this.emailPreferences ?? {};
    this.deactivatedAt = data.deactivatedAt ?? this.deactivatedAt ?? null;
    this.deactivatedBy = data.deactivatedBy ?? this.deactivatedBy ?? null;
    this.deactivationReason =
      data.deactivationReason ?? this.deactivationReason ?? null;
    this.previousRole = data.previousRole ?? this.previousRole ?? null;
  }

  /**
   * Converts the UserProfile instance to a database-friendly format.
   * Never includes the deactivation columns (READ_ONLY_COLUMNS): a routine save
   * from a stale copy must not undo or forge a deactivation.
   * @returns {Object} The user profile data ready for database operations.
   */
  toDatabase() {
    return {
      ...super.toDatabase(),
      auth: this.auth,
      first_name: this.firstName,
      last_name: this.lastName,
      nickname: this.nickname,
      email: this.email,
      personal_email: this.personalEmail,
      phones: this.phones,
      profile_photo: this.profilePhoto,
      active: this.active,
      metrics: this.metrics,
      levy: this.levy,
      //display_id: this.displayId, // Include display_id in the database format
      bohsa_admin: this.bohsaAdmin,
      company_admin: this.companyAdmin,
      company_id: this.companyId,
      trainer_id: this.trainerId,
      role: this.role?.id,
      crm_preferences: this.crmPreferences,
      date_of_birth: this.dateOfBirth,
      national_insurance: this.nationalInsurance,
      home_address: this.homeAddress,
      metadata: this.metadata,
      current_employee: this.currentEmployee,
      current_trainer: this.currentTrainer,
      email_preferences: this.emailPreferences
    };
  }

  /**
   * Get a record by its display ID.
   * @param {string|number} id - The ID of the record to fetch.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  static async getById(id) {
    const profile = await this.fetchOne('display_id', id, this.selectQuery);
    return profile.mergedIntoId ? this.fetchOne('id', profile.mergedIntoId, this.selectQuery) : profile;
  }
  /**
   * Get a record by its unique id
   * @param {string|number} id - The ID of the record to fetch.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  static async getByUniqueId(id) {
    const profile = await this.fetchOne('id', id, this.selectQuery);
    return profile.mergedIntoId ? this.fetchOne('id', profile.mergedIntoId, this.selectQuery) : profile;
  }

  static async getAll(filters = {}, ...args) {
    return super.getAll({ ...filters, merged_into_id: { value: null, operator: 'is' } }, ...args);
  }

  /**
   * Fetch a user profile by auth ID.
   * @param {string} id - The auth ID to fetch.
   * @returns {Promise<UserProfile>} A promise that resolves to a new instance of UserProfile.
   */
  static async getByAuthId(id, activeOnly = false) {
    const filters = { auth: { value: id } };
    if (activeOnly) {
      filters.active = { value: true };
    }
    return this.getAll(filters);
  }

  /**
   * Check if the user has a specific permission for a given entity.
   * @param {number} entityId - The ID of the entity to check permissions for.
   * @param {number} permissionFlag - The permission flag to check (e.g., 1 for read, 2 for write, 4 for update, 8 for delete).
   * @returns {boolean} True if the user has the specified permission, false otherwise.
   */
  hasPermission(entityId, permissionFlag) {
    // Check if the user has permissions for the given entity
    if (this.role.permissions && this.role.permissions[entityId]) {
      // Perform bitwise AND operation to check if the permission flag is set
      return (
        (this.role.permissions[entityId] & permissionFlag) === permissionFlag
      );
    }
    return false; // No permissions found for the entity
  }

  /** permission_groups.id of the "Admin" role, the restricted system-admin tier. */
  static ADMIN_ROLE_ID = 4;

  /**
   * Whether this profile is in the restricted admin tier (full admin-settings
   * access + higher-risk functions): it holds the Admin role itself. Based on
   * the role id, NOT the role's hierarchy (which only orders roles) and NOT the
   * bohsa_admin flag, so a dangling admin flag on a demoted user does not grant
   * elevated access.
   * @returns {boolean}
   */
  isSystemAdmin() {
    return Number(this.role?.id) === UserProfile.ADMIN_ROLE_ID;
  }

  /**
   * Whether this profile may enter the admin settings area at all (true when the
   * role grants any Settings permission).
   * @returns {boolean}
   */
  canAccessSettings() {
    const bits = this.role?.permissions?.[EntityTypeEnum.Settings];
    return typeof bits === 'number' && bits > 0;
  }

  /**
   * Update this profile. Overrides BaseModel.update to strip the admin-controlled
   * fields `role` and `bohsa_admin` from the payload: routine profile saves must
   * never (re)write them, otherwise a session holding a stale profile clobbers a
   * server-side role change (and it would let a user rewrite their own role).
   * Use UserProfile.setAccess() for legitimate role / admin changes.
   * @param {Object} updatedFields - The fields to update.
   * @returns {Promise<UserProfile>}
   */
  async update(updatedFields) {
    Object.assign(this, updatedFields);
    const payload = this.toDatabase();
    delete payload.role;
    delete payload.bohsa_admin;
    READ_ONLY_COLUMNS.forEach(column => delete payload[column]);

    const { data, error } = await supabase
      .from(UserProfile.table)
      .update(payload)
      .eq('id', this.id)
      .select(UserProfile.selectQuery);

    if (error) {
      console.error('[Error] Updating UserProfile: ', error);
      throw error;
    }
    return data ? UserProfile.fromDatabase(data[0]) : undefined;
  }

  /**
   * Admin-only: set a profile's role and/or BOHSA-admin flag directly. Kept out
   * of the normal update()/toDatabase() path so these fields can only be changed
   * deliberately (Team Members / Roles), never piggy-backed on a profile save.
   * @param {string} id - user_profiles.id (uuid)
   * @param {{role?: number, bohsaAdmin?: boolean}} access
   * @returns {Promise<UserProfile|null>}
   */
  static async setAccess(id, { role, bohsaAdmin } = {}) {
    const patch = { updated_date: new Date().toISOString() };
    if (role !== undefined) patch.role = role;
    if (bohsaAdmin !== undefined) patch.bohsa_admin = bohsaAdmin;

    const { data, error } = await supabase
      .from(this.table)
      .update(patch)
      .eq('id', id)
      .select(this.selectQuery);

    if (error) {
      console.error('[Error] Setting UserProfile access: ', error);
      throw error;
    }
    return data?.[0] ? this.fromDatabase(data[0]) : null;
  }
}

export default UserProfile;
